/* ========================================================================
 * 🔴 PROHIBIDO MODIFICAR ESTILOS VISUALES - ZONA INTOCABLE 🔴
 * ========================================================================
 * AUTOR: Trae AI
 * FECHA: 22 de Mayo de 2026
 * IMPORTANCIA: Este archivo controla los chips de filtrado ("Nuevos", "Míos", "Recepcionista").
 * Los estilos de contraste (chipActive) han sido forzados explícitamente a blanco (#ffffff !important)
 * para los textos internos (.MuiChip-label).
 * REGLA: CUALQUIER INTENTO DE ELIMINAR O ALTERAR EL COLOR DEL TEXTO BLANCO 
 * EN EL ESTADO ACTIVO REQUIERE AUTORIZACIÓN EXPLÍCITA Y POR ESCRITO DEL USUARIO.
 * ======================================================================== */
import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsWhatsappSelect from "../TicketsWhatsappSelect";
import { Button, IconButton, Chip, Menu, MenuItem, Fade, Tooltip } from "@material-ui/core";
import { Add, FilterList, History, Inbox, MoreVert, Wifi, WifiOff } from "@material-ui/icons";
import useWhatsApps from "../../hooks/useWhatsApps";
import api from "../../services/api";
import openSocket from "../../services/socket-io";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(2), // Aumento significativo del espacio superior
  },
  headerContainer: {
    padding: "16px 16px 16px 16px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    zIndex: 1,
    borderBottom: `1px solid ${theme.palette.divider}`, // Separador visual sutil
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mainTitle: {
    fontWeight: 700,
    fontSize: "1.2rem",
    color: theme.palette.text.primary,
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: "6px 12px",
    margin: theme.spacing(0, 2, 1, 2),
    border: `1px solid ${theme.palette.divider}`,
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  chipsContainer: {
    display: "flex",
    gap: theme.spacing(1),
    overflowX: "auto",
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingRight: 10, // Espacio extra a la derecha para badges
    "&::-webkit-scrollbar": {
      display: "none"
    }
  },
  chip: {
    fontWeight: 500,
    fontSize: "0.85rem",
    borderRadius: 8,
    height: 36,
    padding: "0 10px", // Más padding interno para que el texto no choque con el badge
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "all 0.2s",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    }
  },
  chipActive: {
    backgroundColor: theme.palette.primary.main + " !important",
    color: "#ffffff !important",
    "& .MuiChip-label, & span": {
        color: "#ffffff !important",
    },
    "&:hover": {
        backgroundColor: theme.palette.primary.dark + " !important",
    }
  },
  chipOutlined: {
    backgroundColor: "transparent",
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  badge: {
    right: 3, // Mover hacia adentro
    top: 3,   // Mover hacia abajo
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    backgroundColor: 'red', // Color más visible
    color: 'white'
  },
  subChipsContainer: {
    display: "flex",
    gap: theme.spacing(1),
    overflowX: "auto",
    padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    marginBottom: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.type === 'dark' ? "rgba(125,60,152,0.06)" : "#f7f1fb",
  },
  subChip: {
    fontWeight: 500,
    fontSize: "0.8rem",
    borderRadius: 6,
    height: 30,
    padding: "0 8px",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": { backgroundColor: theme.palette.action.hover }
  },
  subChipActive: {
    backgroundColor: "#7d3c98 !important",
    color: "#ffffff !important",
    "& .MuiChip-label, & span": { color: "#ffffff !important" },
  },
  subChipOutlined: {
    backgroundColor: "transparent",
    border: `1px solid #b78ec9`,
    color: "#5b2a74",
  },
  connectionStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 16,
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: theme.palette.type === 'dark' ? "rgba(255,255,255,0.05)" : "#f1f3f4",
    marginRight: 10,
  },
  statusOnline: {
    color: '#4caf50',
  },
  statusOffline: {
    color: '#f44336',
  }
}));

const TicketsManager = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open"); // open | closed | search
  const [filterType, setFilterType] = useState("all"); // all | pending | mine | ai
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const { whatsApps } = useWhatsApps();

  // Contadores
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [aiCount, setAiCount] = useState(0);
  const [followupCount, setFollowupCount] = useState(0);

  // Seguimiento: conteo real + por conexión
  const [followupByWhatsapp, setFollowupByWhatsapp] = useState({});
  const [followUpWhatsappIdFilter, setFollowUpWhatsappIdFilter] = useState(""); // "" = todas

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);

  // Menu de filtros extra
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
  }, []);

  // Cargar conteo REAL de Seguimiento (contactos DISTINCTOS, no tickets)
  const loadFollowUpCounts = async () => {
    try {
      const { data } = await api.get("/tickets-followup-count");
      setFollowupCount(Number(data.total || 0));
      setFollowupByWhatsapp(data.byWhatsappId || {});
    } catch (err) {
      // Silencioso: no interrumpir la UI si falla
    }
  };

  useEffect(() => {
    loadFollowUpCounts();
    const t = setInterval(loadFollowUpCounts, 30 * 1000);

    // Actualización EN TIEMPO REAL del contador de Seguimiento vía Socket.IO
    let socket;
    try {
      socket = openSocket();
      socket.on("followup:countChanged", (payload) => {
        loadFollowUpCounts();
      });
    } catch (err) {
      // Silencioso: fallback es el setInterval de 30s
    }

    return () => {
      clearInterval(t);
      try { if (socket) socket.disconnect(); } catch (e) {}
    };
  }, []);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      return;
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const applyPanelStyle = (currentFilter) => {
    if (tab !== "open" || filterType !== currentFilter) {
      return { width: 0, height: 0, opacity: 0, position: 'absolute', pointerEvents: 'none' };
    }
    return {};
  };

  const handleFilterMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setAnchorEl(null);
  };

  // Obtener la conexión del usuario (solo si no es admin y tiene una asignada)
  const userWhatsapp = whatsApps.find(w => w.id === user.whatsappId);
  const isOnline = userWhatsapp?.status === "CONNECTED";

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(e) => setNewTicketModalOpen(false)}
      />
      
      {/* Modern Header Section */}
      <div className={classes.headerContainer}>
        
        {/* 1. Top Bar: Title & Main Actions */}
        <div className={classes.topBar}>
            <div style={{ display: 'flex', gap: 10 }}>
                <Button 
                    size="small" 
                    onClick={() => setTab("open")}
                    style={{ 
                        fontWeight: tab === "open" ? 700 : 400,
                        color: tab === "open" ? (theme.palette.type === 'dark' ? '#fff' : theme.palette.text.primary) : theme.palette.text.secondary,
                        borderBottom: tab === "open" ? `2px solid ${theme.palette.type === 'dark' ? '#fff' : theme.palette.text.primary}` : "none",
                        borderRadius: 0,
                        padding: "0 4px"
                    }}
                >
                    Chats
                </Button>
                <Button 
                    size="small" 
                    onClick={() => setTab("closed")}
                    style={{ 
                        display: "none",
                        fontWeight: tab === "closed" ? 700 : 400,
                        color: tab === "closed" ? (theme.palette.type === 'dark' ? '#fff' : theme.palette.text.primary) : theme.palette.text.secondary,
                        borderBottom: tab === "closed" ? `2px solid ${theme.palette.type === 'dark' ? '#fff' : theme.palette.text.primary}` : "none",
                        borderRadius: 0,
                        padding: "0 4px"
                    }}
                >
                    Historial
                </Button>
                <Button 
                    size="small" 
                    onClick={() => setTab("search")}
                    style={{ 
                        display: "none",
                        fontWeight: tab === "search" ? 700 : 400,
                        color: tab === "search" ? (theme.palette.type === 'dark' ? '#fff' : theme.palette.text.primary) : theme.palette.text.secondary,
                        borderBottom: tab === "search" ? `2px solid ${theme.palette.type === 'dark' ? '#fff' : theme.palette.text.primary}` : "none",
                        borderRadius: 0,
                        padding: "0 4px"
                    }}
                >
                    Buscar
                </Button>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* Indicador de conexión para usuarios normales */}
                {user.profile !== "admin" && userWhatsapp && (
                  <Tooltip title={isOnline ? "Conexión estable" : "WhatsApp desconectado. Revisa la conexión."}>
                    <div className={classes.connectionStatus}>
                      {isOnline ? 
                        <Wifi className={classes.statusOnline} fontSize="small" /> : 
                        <WifiOff className={classes.statusOffline} fontSize="small" />
                      }
                      <span className={isOnline ? classes.statusOnline : classes.statusOffline}>
                        {userWhatsapp.name}
                      </span>
                    </div>
                  </Tooltip>
                )}

                <IconButton size="small" onClick={handleFilterMenuClick}>
                    <FilterList fontSize="small" />
                </IconButton>
                <IconButton style={{ display: "none" }} size="small" onClick={() => setNewTicketModalOpen(true)} color="primary">
                    <Add fontSize="small" />
                </IconButton>
            </div>
        </div>

        {/* 2. Search Bar */}
        <div className={classes.searchContainer}>
          <SearchIcon style={{ color: "#bdbdbd" }} fontSize="small" />
          <InputBase
            className={classes.searchInput}
            inputRef={searchInputRef}
            placeholder="Buscar..."
            type="search"
            onChange={handleSearch}
          />
        </div>

        {/* 3. Smart Chips Filter (Only for Open Chats) */}
        {tab === "open" && (
          <>
            <div className={classes.chipsContainer}>
                <Badge badgeContent={pendingCount} max={99999} color="secondary" overlap="rectangular" classes={{ badge: classes.badge }}>
                    <Chip
                        label="Nuevos"
                        onClick={() => { setFilterType("pending"); setFollowUpWhatsappIdFilter(""); }}
                        className={`${classes.chip} ${filterType === "pending" ? classes.chipActive : classes.chipOutlined}`}
                    />
                </Badge>

                <Badge badgeContent={openCount} max={99999} color="primary" overlap="rectangular" classes={{ badge: classes.badge }}>
                    <Chip
                        label="Míos"
                        onClick={() => { setFilterType("mine"); setFollowUpWhatsappIdFilter(""); }}
                        className={`${classes.chip} ${filterType === "mine" ? classes.chipActive : classes.chipOutlined}`}
                    />
                </Badge>

                <Badge badgeContent={aiCount} max={99999} color="default" overlap="rectangular" classes={{ badge: classes.badge }}>
                    <Chip
                        label="Recepcionista"
                        onClick={() => { setFilterType("ai"); setFollowUpWhatsappIdFilter(""); }}
                        className={`${classes.chip} ${filterType === "ai" ? classes.chipActive : classes.chipOutlined}`}
                    />
                </Badge>

                <Badge badgeContent={followupCount} max={99999} color="secondary" overlap="rectangular" classes={{ badge: classes.badge }}>
                    <Chip
                        label="Seguimiento"
                        onClick={() => setFilterType("followup")}
                        className={`${classes.chip} ${filterType === "followup" ? classes.chipActive : classes.chipOutlined}`}
                    />
                </Badge>
            </div>

            {/* 3.B — Sub-filtros Seguimiento POR CONEXIÓN */}
            {filterType === "followup" && whatsApps.length > 0 && (
              <div className={classes.subChipsContainer}>
                <Badge badgeContent={followupCount} max={99999} color="secondary" overlap="rectangular" classes={{ badge: classes.badge }}>
                  <Chip
                    label="Todas"
                    onClick={() => setFollowUpWhatsappIdFilter("")}
                    className={`${classes.subChip} ${followUpWhatsappIdFilter === "" ? classes.subChipActive : classes.subChipOutlined}`}
                  />
                </Badge>
                {whatsApps.map(w => {
                  const info = followupByWhatsapp[w.id];
                  const cnt = info ? Number(info.count || 0) : 0;
                  return (
                    <Badge key={w.id} badgeContent={cnt} max={99999} color="secondary" overlap="rectangular" classes={{ badge: classes.badge }}>
                      <Chip
                        label={w.name}
                        onClick={() => setFollowUpWhatsappIdFilter(String(w.id))}
                        className={`${classes.subChip} ${followUpWhatsappIdFilter === String(w.id) ? classes.subChipActive : classes.subChipOutlined}`}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Menu for extra filters (Connections, Show All) */}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleFilterMenuClose}
        TransitionComponent={Fade}
      >
        <MenuItem>
             <Can
              role={user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                  <FormControlLabel
                    label={i18n.t("tickets.buttons.showAll")}
                    labelPlacement="start"
                    control={
                      <Switch
                        size="small"
                        checked={showAllTickets}
                        onChange={() => setShowAllTickets((prevState) => !prevState)}
                        color="primary"
                      />
                    }
                  />
              )}
            />
        </MenuItem>
        
        {/* Solo mostrar selector de WhatsApps a los administradores */}
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <MenuItem>
                <TicketsWhatsappSelect
                  style={{ width: 150 }}
                  selectedWhatsappIds={selectedWhatsappIds}
                  onChange={(values) => setSelectedWhatsappIds(values)}
                />
            </MenuItem>
          )}
        />
      </Menu>

      {/* Content Area */}
      <div className={classes.ticketsWrapper}>
        
        {/* PENDIENTES (NUEVOS) */}
        <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            selectedWhatsappIds={selectedWhatsappIds}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
        />

        {/* MÍOS (OPEN) */}
        <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            selectedWhatsappIds={selectedWhatsappIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("mine")}
        />

        {/* IA (BOT) */}
        <TicketsList
            status="open"
            isBot="true"
            showAll={user.profile === "admin"} 
            selectedQueueIds={selectedQueueIds}
            selectedWhatsappIds={selectedWhatsappIds}
            updateCount={(val) => setAiCount(val)}
            style={applyPanelStyle("ai")}
        />

        {/* SEGUIMIENTO */}
        <TicketsList
            status="open"
            isBot="true"
            isFollowUp="true"
            followUpWhatsappId={followUpWhatsappIdFilter}
            showAll={true}
            selectedQueueIds={selectedQueueIds}
            selectedWhatsappIds={selectedWhatsappIds}
            externalCount={
              followUpWhatsappIdFilter === ""
                ? Number(followupCount || 0)
                : (followupByWhatsapp[followUpWhatsappIdFilter] ? Number(followupByWhatsapp[followUpWhatsappIdFilter].count || 0) : 0)
            }
            updateCount={(val) => {
              if (followUpWhatsappIdFilter === "") setFollowupCount(val);
            }}
            style={applyPanelStyle("followup")}
        />

        {/* HISTORIAL (CLOSED) */}
        {tab === "closed" && (
            <TicketsList
                status="closed"
                showAll={true}
                selectedQueueIds={selectedQueueIds}
                selectedWhatsappIds={selectedWhatsappIds}
            />
        )}

        {/* BÚSQUEDA GLOBAL */}
        {searchParam.length > 0 && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, width: '100%', backgroundColor: theme.palette.background.default, zIndex: 10 }}>
            <TicketsList
                    searchParam={searchParam}
                    showAll={true}
                    selectedQueueIds={selectedQueueIds}
                    selectedWhatsappIds={selectedWhatsappIds}
                />
            </div>
        )}
      </div>
    </Paper>
  );
};

export default TicketsManager;
